import { useState } from 'react';
import axios from 'axios';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  useToast,
} from '@chakra-ui/react';
import { loginAdmin } from '../../services/adminAuth';
import { getApiUrl } from '../../config/config';

const AdminLoginModal = ({ isOpen, onClose, setIsAdmin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Handle Enter key press - Fixed to not pass event to handleLogin
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && username && password && !loading) {
      handleLogin(); // Don't pass the event
    }
  };

  // Fixed handleLogin to make event parameter optional
const handleLogin = async () => {
    setLoading(true);
    try {
      // Use the dedicated service for the API call
      const response = await axios.post(`${getApiUrl()}api/auth/user_cred`, {
        username: username.trim(),
        password: password.trim()
      });

      // Check if the response contains the token from our backend
      if (response.data && response.data.token) {
        // Call the central login function with the token
        loginAdmin(response.data.token);
        if(setIsAdmin) setIsAdmin(true);

        toast({
          title: "Login Successful",
          description: "Welcome back!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        handleClose(); // Close the modal on success
      } else {
        // This case handles a successful (2xx) response that is malformed
        throw new Error("Login failed: Invalid response from server.");
      }
    } catch (error) {
      // This block handles network errors and 4xx/5xx responses from axios
      let errorMessage = "Could not connect to the server.";
      if (error.response) {
        // The server responded with an error status code
        if (error.response.status === 401) {
          errorMessage = "Invalid username or password.";
        } else {
          errorMessage = `Login failed with status: ${error.response.status}`;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = "No response from server. Please check your connection.";
      } else {
        // Something else happened
        errorMessage = error.message;
      }

      toast({
        title: "Login Failed",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUsername('');
    setPassword('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Admin Login</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel>Username</FormLabel>
            <Input 
              value={username} 
              onChange={(e) => setUsername(e.target.value)} 
              onKeyPress={handleKeyPress}
              autoComplete="username"
              placeholder="Enter username"
            />
          </FormControl>
          <FormControl mt={4}>
            <FormLabel>Password</FormLabel>
            <Input 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              onKeyPress={handleKeyPress}
              autoComplete="current-password"
              placeholder="Enter password"
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button 
            colorScheme="blue" 
            mr={3} 
            onClick={handleLogin} // This will pass the click event
            isLoading={loading}
            loadingText="Logging in..."
            isDisabled={!username || !password}
          >
            Login
          </Button>
          <Button variant="ghost" onClick={handleClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdminLoginModal;